<template>
  <div class="page-container animate-fade-in-up">
    <PageHeader icon="el-icon-s-tools" title="评估规则配置" subtitle="集中管理评分规则、风险阈值等系统配置" />

    <!-- ===== 上区：风险等级 + 评分规则说明 ===== -->
    <div class="top-section">
      <!-- 左侧：风险阈值配置 -->
      <div class="card risk-card">
        <div class="section-title">风险等级预览</div>
        <div class="risk-preview">
          <div class="risk-row">
            <span class="risk-dot" style="background: var(--color-low);"></span>
            <span class="risk-label">低风险：</span>
            <span class="risk-range">0-{{ scoringForm.lowMax }}分</span>
          </div>
          <div class="risk-row">
            <span class="risk-dot" style="background: var(--color-medium);"></span>
            <span class="risk-label">中风险：</span>
            <span class="risk-range">{{ scoringForm.lowMax + 1 }}-{{ scoringForm.mediumMax }}分</span>
          </div>
          <div class="risk-row">
            <span class="risk-dot" style="background: var(--color-high);"></span>
            <span class="risk-label">高风险：</span>
            <span class="risk-range">&ge;{{ scoringForm.mediumMax + 1 }}分</span>
          </div>
        </div>

        <div class="threshold-inputs">
          <div class="input-item">
            <div class="input-label">低风险上限</div>
            <el-input-number v-model="scoringForm.lowMax" :min="0" :max="999" />
          </div>
          <div class="input-item">
            <div class="input-label">中风险上限</div>
            <el-input-number v-model="scoringForm.mediumMax" :min="1" :max="999" />
          </div>
        </div>

        <div v-if="$hasPermission('scoring:edit')" class="section-actions">
          <el-button type="primary" class="btn-click" :loading="savingThresholds" @click="saveThresholds">
            <i class="el-icon-check"></i> 保存配置
          </el-button>
          <el-button class="btn-click" @click="resetScoring">重置为默认</el-button>
        </div>
      </div>

      <!-- 右侧：评分规则说明 -->
      <div class="card rules-card">
        <div class="section-title">评分规则说明</div>
        <div class="section-subtitle">前台评估页右侧分数面板展示的评分规则文本</div>
        <SimpleRichEditor v-model="scoringForm.content" />
        <div class="setting-hint">
          <i class="el-icon-info"></i> 保存后，前台评估页面将自动显示最新的评分规则
        </div>
        <div v-if="$hasPermission('scoring:edit')" class="section-actions right-actions">
          <el-button type="primary" class="btn-click" :loading="savingRules" @click="saveScoringRules">
            <i class="el-icon-check"></i> 保存规则
          </el-button>
          <el-button class="btn-click" @click="resetScoringRules">重置为默认</el-button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import PageHeader from '../../components/PageHeader.vue'
import SimpleRichEditor from '../../components/SimpleRichEditor.vue'

export default {
  name: 'ConfigCenter',
  components: { PageHeader, SimpleRichEditor },
  data() {
    return {
      scoringForm: {
        lowMax: 4,
        mediumMax: 10,
        content: ''
      },
      savingThresholds: false,
      savingRules: false
    }
  },
  watch: {
    '$store.state.scoringRules.rules': {
      immediate: true,
      handler() {
        this.initScoringForm()
      }
    }
  },
  created() {
    this.initScoringForm()
  },
  methods: {
    // ===== 评分规则 =====
    initScoringForm() {
      const s = this.$store.state.settings.settings
      this.scoringForm.lowMax = s.thresholds ? s.thresholds.lowMax : 4
      this.scoringForm.mediumMax = s.thresholds ? s.thresholds.mediumMax : 10
      const rules = this.$store.state.scoringRules.rules
      this.scoringForm.content = rules && rules.content ? rules.content : ''
    },
    async saveThresholds() {
      const { lowMax, mediumMax } = this.scoringForm
      if (lowMax < 0) {
        this.$message.error('低风险上限不能为负数')
        return
      }
      if (mediumMax < 1) {
        this.$message.error('中风险上限不能小于 1')
        return
      }
      if (lowMax >= mediumMax) {
        this.$message.error('低风险上限必须小于中风险上限')
        return
      }
      this.savingThresholds = true
      const taskId = this.$store.getters.currentTaskId
      try {
        await this.$api.updateSetting({
          task_id: taskId || null,
          config_key: 'settings',
          config_value: {
            thresholds: { lowMax: this.scoringForm.lowMax, mediumMax: this.scoringForm.mediumMax },
            labels: { low: '低风险', medium: '中风险', high: '高风险' },
            system: { name: 'AI骨干人才保留助手', version: 'v3.1' }
          }
        })

        // 重新加载配置
        const res = await this.$api.fetchSettings()
        if (res.data) {
          const rawList = res.data
          const settingsItem = rawList.find(s => s.config_key === 'settings')
          if (settingsItem && settingsItem.config_value) {
            const s = settingsItem.config_value
            if (taskId) {
              this.$store.commit('SET_SETTINGS_FOR_TASK', { taskId, settings: s })
            } else {
              this.$store.commit('SET_SETTINGS', s)
            }
          }
        }

        // 重新加载员工数据（后端已按新阈值刷新风险等级）
        const empRes = await this.$api.fetchEmployees()
        if (empRes.data) {
          this.$store.commit('SET_EMPLOYEES', empRes.data)
        }

        this.$message.success('阈值配置已保存，员工风险等级已更新')
      } catch (e) {
        this.$message.error('保存失败：' + ((e.response && e.response.data && e.response.data.message) || e.message || '未知错误'))
      } finally {
        this.savingThresholds = false
      }
    },

    async saveScoringRules() {
      this.savingRules = true
      const taskId = this.$store.getters.currentTaskId
      try {
        await this.$api.updateSetting({
          task_id: taskId || null,
          config_key: 'scoring_rules',
          config_value: {
            content: this.scoringForm.content,
            updated_at: new Date().toISOString().replace('T', ' ').slice(0, 19)
          }
        })

        const res = await this.$api.fetchSettings()
        if (res.data) {
          const rulesItem = res.data.find(s => s.config_key === 'scoring_rules')
          if (rulesItem && rulesItem.config_value) {
            const rules = rulesItem.config_value
            if (taskId) {
              this.$store.commit('SET_RULES_FOR_TASK', { taskId, rules })
            } else {
              this.$store.commit('SET_SCORING_RULES', rules)
            }
          }
        }
        this.$message.success('评分规则已保存')
      } catch (e) {
        this.$message.error('保存失败：' + ((e.response && e.response.data && e.response.data.message) || e.message || '未知错误'))
      } finally {
        this.savingRules = false
      }
    },
    resetScoring() {
      this.$confirm('确定重置为默认配置？', '提示').then(() => {
        const taskId = this.$store.getters.currentTaskId
        const defaultSettings = {
          thresholds: { lowMax: 4, mediumMax: 10 },
          labels: { low: '低风险', medium: '中风险', high: '高风险' },
          system: { name: 'AI骨干人才保留助手', version: 'v3.1' }
        }
        if (taskId) {
          this.$store.commit('SET_SETTINGS_FOR_TASK', { taskId, settings: defaultSettings })
        } else {
          this.$store.commit('SET_SETTINGS', defaultSettings)
        }
        this.scoringForm.lowMax = defaultSettings.thresholds.lowMax
        this.scoringForm.mediumMax = defaultSettings.thresholds.mediumMax
        this.$message.success('风险等级配置已重置')
      }).catch(() => {})
    },
    resetScoringRules() {
      this.$confirm('确定重置评分规则说明为默认文本？', '提示').then(() => {
        const taskId = this.$store.getters.currentTaskId
        const defaultRules = {
          rule_id: 1,
          content: '加分项表明员工存在离职倾向，勾选"是"即累加对应分值。\n\n0-4分：低风险\n5-10分：中风险\n≥11分：高风险',
          updated_at: new Date().toISOString().replace('T', ' ').slice(0, 19)
        }
        if (taskId) {
          this.$store.commit('SET_RULES_FOR_TASK', { taskId, rules: defaultRules })
        } else {
          this.$store.commit('SET_SCORING_RULES', defaultRules)
        }
        this.scoringForm.content = defaultRules.content
        this.$message.success('评分规则说明已重置')
      }).catch(() => {})
    },

  }
}
</script>

<style scoped>
/* ===== 上区：双栏布局 ===== */
.top-section {
  display: flex;
  gap: var(--space-2xl);
  margin-bottom: var(--space-2xl);
}

/* 通用卡片 */
.card {
  padding: var(--space-xl);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
}

.risk-card {
  flex: 0 0 340px;
}
.rules-card {
  flex: 1 1 auto;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: var(--space-lg);
}
.section-subtitle {
  font-size: 13px;
  color: var(--color-text-secondary);
  margin-bottom: var(--space-md);
}

/* 风险等级预览 */
.risk-preview {
  padding: var(--space-lg);
  background: var(--color-bg);
  border-radius: var(--radius-md);
  margin-bottom: var(--space-xl);
}
.risk-row {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  font-size: 14px;
  margin-bottom: var(--space-sm);
}
.risk-row:last-child { margin-bottom: 0; }
.risk-dot {
  width: 10px;
  height: 10px;
  border-radius: var(--radius-full);
  flex-shrink: 0;
}
.risk-label { font-weight: 600; color: var(--color-text); }
.risk-range { color: var(--color-text-secondary); }

/* 阈值输入 */
.threshold-inputs {
  display: flex;
  flex-direction: column;
  gap: var(--space-lg);
  margin-bottom: var(--space-xl);
}
.input-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--color-text-secondary);
  margin-bottom: var(--space-sm);
}

/* 操作按钮 */
.section-actions {
  display: flex;
  gap: var(--space-md);
  padding-top: var(--space-lg);
  border-top: 1px solid var(--color-border-light);
}
.right-actions {
  border-top: none;
  padding-top: var(--space-md);
  margin-top: var(--space-md);
}

/* 提示文字 */
.setting-hint {
  margin-top: 8px;
  font-size: 12px;
  color: var(--color-text-muted);
}

</style>
